import type { IMonitorReplay, IReplayEvent, IReplayFrame } from '@interfaces/IMonitorApi';
import type { ReplaySafetyEvent } from './replayRail';

/** A day's instants, parsed once per payload so a playback tick never re-parses it. */
export interface IReplayIndex {
  frames: IReplayFrame[];
  frameMs: number[];
  /** Events sorted by time, with their instants. */
  events: IReplayEvent[];
  eventMs: number[];
  /** The safety alerts among them, in the same order, with their instants. */
  safety: ReplaySafetyEvent[];
  safetyMs: number[];
}

/** An event with its parsed instant. */
interface ITimedEvent<T extends IReplayEvent = IReplayEvent> {
  e: T;
  ms: number;
}

const isSafety = (timed: ITimedEvent): timed is ITimedEvent<ReplaySafetyEvent> => timed.e.kind === 'safety';

export function indexReplay(replay: IMonitorReplay): IReplayIndex {
  const events: ITimedEvent[] = replay.events.map((e) => ({ e, ms: Date.parse(e.at) })).sort((a, b) => a.ms - b.ms);
  const safety = events.filter(isSafety);
  return {
    frames: replay.frames,
    frameMs: replay.frames.map((f) => Date.parse(f.t)),
    events: events.map((x) => x.e),
    eventMs: events.map((x) => x.ms),
    safety: safety.map((timed) => timed.e),
    safetyMs: safety.map((timed) => timed.ms),
  };
}

/** Index of the last value <= atMs in an ascending list; -1 before the first. */
function lastAtOrBefore(sorted: number[], atMs: number): number {
  let lo = 0;
  let hi = sorted.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if ((sorted[mid] as number) <= atMs) lo = mid + 1;
    else hi = mid;
  }
  return lo - 1;
}

/** The frame at `at`: across a data gap, the last one before it, as nothing
 *  changed until data resumes. Null only before the first frame. */
export function frameAt(index: IReplayIndex, at: number): IReplayFrame | null {
  return index.frames[lastAtOrBefore(index.frameMs, at)] ?? null;
}

/** How many of the day's safety alerts are at or before `at`. */
export const safetyReached = (index: IReplayIndex, at: number): number => lastAtOrBefore(index.safetyMs, at) + 1;

/** Events with `from < at <= to` — empty for a backwards step. */
export function crossedBetween(index: IReplayIndex, from: number, to: number): IReplayEvent[] {
  return index.events.slice(lastAtOrBefore(index.eventMs, from) + 1, lastAtOrBefore(index.eventMs, to) + 1);
}
